import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { LayoutDashboard, CalendarDays, Columns3, ClipboardList, Flame, Droplets, Search } from "lucide-react";
import { decorationLabel } from "@/lib/labels";

const NAV_ITEMS = [
  { label: "Dashboard", path: "/", icon: LayoutDashboard },
  { label: "Work Orders", path: "/work-orders", icon: ClipboardList },
  { label: "Schedule", path: "/schedule", icon: CalendarDays },
  { label: "Lanes", path: "/lanes", icon: Columns3 },
  { label: "ScreenMaster", path: "/screenmaster", icon: Flame },
  { label: "InkMaster", path: "/inkmaster", icon: Droplets },
];

function decorationStyle(type: string) {
  // Opaque SDK pairs stay readable in either theme without changing job-card colours.
  switch (type) {
    case "screenprint": return { backgroundColor: "var(--pp-teal-700)", color: "var(--pp-paper)" };
    case "embroidery": return { backgroundColor: "var(--pp-teal-950)", color: "var(--pp-paper)" };
    case "heat_press": return { backgroundColor: "var(--pp-warning)", color: "var(--pp-ink)" };
    case "poly_bagging": return { backgroundColor: "var(--pp-olive)", color: "var(--pp-paper)" };
    default: return { backgroundColor: "var(--pp-smoke)", color: "var(--pp-ink)" };
  }
}

interface SearchResult {
  id: string;
  orderNumber: string;
  customerName: string;
  decorationType: string;
  accountManager: string | null;
}

export function CommandSearch({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [query, setQuery] = useState("");
  const [, navigate] = useLocation();
  const debouncedQuery = useDebounce(query, 300);

  const { data: results, isLoading } = useQuery<SearchResult[]>({
    queryKey: ["/api/work-orders/search", debouncedQuery],
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/work-orders/search?q=${encodeURIComponent(debouncedQuery)}`, { credentials: "include", signal });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: debouncedQuery.length >= 2,
  });

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  function selectResult(path: string) {
    onOpenChange(false);
    navigate(path);
  }

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search work orders or navigate..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {debouncedQuery.length < 2 ? "Type to search work orders..." : isLoading ? "Searching..." : "No results found."}
        </CommandEmpty>

        {results && results.length > 0 && (
          <CommandGroup heading="Work Orders">
            {results.map((wo) => (
              <CommandItem
                key={wo.id}
                value={`${wo.orderNumber} ${wo.customerName} ${wo.accountManager ?? ""}`}
                onSelect={() => selectResult(`/work-orders/${wo.id}`)}
                className="gap-2"
              >
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium whitespace-nowrap shrink-0">{wo.orderNumber}</span>
                <span className="text-muted-foreground truncate min-w-0 flex-1">{wo.customerName}</span>
                {wo.accountManager && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">· {wo.accountManager}</span>
                )}
                <Badge
                  className="ml-auto text-[10px] px-1.5 py-0 shrink-0"
                  style={decorationStyle(wo.decorationType)}
                >
                  {decorationLabel(wo.decorationType)}
                </Badge>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Pages">
          {NAV_ITEMS.map((item) => (
            <CommandItem
              key={item.path}
              value={item.label}
              onSelect={() => selectResult(item.path)}
            >
              <item.icon className="h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
